import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { CHANGE_PASSWORD_PATH } from "../auth/RequireAuth";
import "./shell.css";

/**
 * The application frame: identity on the left, page title and the signed-in
 * administrator across the top, work in the middle.
 *
 * Only real destinations appear in navigation. A nav item for a section that
 * does not exist teaches an administrator to distrust the menu — and so does
 * one that answers "you don't have permission" every time it is pressed. So
 * each item is rendered only when the SERVER has said this administrator may
 * read that section.
 *
 * THIS IS NOT AUTHORIZATION. Typing the address of a hidden section reaches a
 * screen that says so, and every request behind it is refused by the API on its
 * own authority. Hiding the item is a courtesy to the operator, not a control:
 * the capability list arrives from the server and is used to draw, never to
 * decide.
 */
export function Shell({
  title,
  actions,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { email, displayName, signOut, can } = useAuth();

  return (
    <div className="shell">
      <aside className="shell__sidebar">
        <div className="shell__brand">
          <span className="shell__mark" aria-hidden="true" />
          <span className="shell__brandName">OptiQ Admin</span>
        </div>
        <nav className="shell__nav" aria-label="Sections">
          {/* The two places an administrator stands — Orders and Products —
              then the actions taken from one of them. `end` on each stops it
              highlighting on every route beneath it.

              Orders comes first among the sections because it is the daily
              one: the catalogue is edited occasionally, and orders arrive
              every day and have to be moved. */}
          {can(CAP.dashboardRead) ? (
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Dashboard
            </NavLink>
          ) : null}
          {/* A section, not an action: a place an operator works in.
              Deliberately WITHOUT `end`, unlike Dashboard and Products.

              One order's own screen is INSIDE this section, not somewhere
              else, so `/orders/:orderId` must keep Orders lit. With `end` the
              sidebar went blank the moment an operator opened an order, which
              reads as having left the section they are plainly still in.

              Products keeps `end` because its sub-routes are a different
              case: `/products/add` is an action with its own nav item, and
              having both light at once would say the operator is in two
              places. */}
          {can(CAP.ordersRead) ? (
            <NavLink
              to="/orders"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Orders
            </NavLink>
          ) : null}
          {/* A section, and deliberately placed between Orders and Products.
              It is where an operator stands when they are dealing with the
              shelf rather than the catalogue, and that happens on the way from
              an order much more often than on the way from a product page.

              Like Orders it is WITHOUT `end`: a filtered or paged view is
              still this section, and the sidebar going blank on one would read
              as having left a place the operator is plainly still in. */}
          {can(CAP.inventoryRead) ? (
            <NavLink
              to="/inventory"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Inventory
            </NavLink>
          ) : null}
          {/* Product Management had no way in until now: the screen existed but
              nothing in the menu reached it, so an administrator could only
              arrive by typing the address. */}
          {can(CAP.productsRead) ? (
            <NavLink
              to="/products"
              end
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Products
            </NavLink>
          ) : null}
          {/* Editing and archiving are things done *to* a product, so they are
              reached by choosing one from Products — not by picking an action
              first and hunting for the product second. There is no nav item for
              either, because neither is a place. */}
          {can(CAP.productsWrite) ? (
            <NavLink
              to="/products/add"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Add Product
            </NavLink>
          ) : null}
          {/* A section, and placed after Products because it is a view ACROSS
              the catalogue rather than a way into one product: an operator
              arrives here having decided to look at pricing, not having decided
              which product to look at. Deliberately WITHOUT `end`, like Orders
              and Inventory — a filtered or paged view is still this section.

              It carries no sub-routes: a discount is one column on a product's
              working revision, so the place to inspect or publish one is the
              product's own workspace. */}
          {/* Reading what things cost is reading the catalogue, so this follows
              `products.read`. Setting or clearing a discount is a product
              write and is gated on the controls themselves. */}
          {can(CAP.productsRead) ? (
            <NavLink
              to="/discounts"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Discounts
            </NavLink>
          ) : null}
          {/* Last, and after both single-product entries, because that is the
              order the work happens in: an operator learns the catalogue one
              product at a time and reaches for a workbook when there are too
              many to do that way. It is a first-class section rather than an
              action inside Products, because it is a place — a file is chosen,
              checked and confirmed there — and not something done to one
              product. */}
          {can(CAP.bulkRead) ? (
            <NavLink
              to="/bulk-products"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Bulk Products
            </NavLink>
          ) : null}
          {/* Last, and by itself: this is the only section that is about the
              people using the tool rather than about the shop. There is no
              Audit item beside it yet — that read model is its own phase, and a
              menu entry for a screen that does not exist teaches an
              administrator to distrust the menu. */}
          {can(CAP.usersRead) ? (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                isActive ? "shell__navItem is-active" : "shell__navItem"
              }
            >
              Users
            </NavLink>
          ) : null}
        </nav>
      </aside>

      <div className="shell__main">
        <header className="shell__header">
          <h1 className="shell__title">{title}</h1>
          <div className="spacer" />
          {actions}
          <div className="shell__identity">
            <span className="shell__admin" title={email ?? undefined}>
              {displayName || email || "Administrator"}
            </span>
            {/* Behind no capability, because it is about this account rather
                than about the shop: every administrator may change their own
                password, and an account that could not would have no way to
                stop using one somebody else knows. */}
            <NavLink to={CHANGE_PASSWORD_PATH} className="btn btn--subtle btn--sm">
              Change password
            </NavLink>
            <button
              type="button"
              className="btn btn--subtle btn--sm"
              onClick={() => void signOut()}
            >
              Log out
            </button>
          </div>
        </header>
        <main className="shell__content">{children}</main>
      </div>
    </div>
  );
}
